import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CAMPAIGNS_BUDGET, CAMPAIGNS_STATUSES, CAMPAIGNS_TYPES } from 'const';
import {
	CampaignPriceTypes,
	CampaignTypes,
	ICampaign,
	CampaignPeriodTypes,
	ICampaignPrice,
	ICampaignPrices,
	ICampaignPriceData,
	IPrices,
} from 'interfaces/campaigns';
import {
	getAdvertisingCampaignById,
	payAdvertisingCampaign,
} from 'services/advertisingCampaigns';
import {
	checkAdvertisingCampaignStatus,
	startAdvertisingCampaign,
} from 'services/advertisingCampaigns';
import {
	yandexAdvertisingPrice,
	yandexActivatePromocode,
	yandexDeactivatePromocode,
} from 'services/yandex';
import { useSelector } from 'store';
import { selectBalance } from 'store/balanceReducer';
import {
	selectCompanyProduct,
	selectCompanyProductSubclientId,
	selectHasCompanyRights,
} from 'store/companiesReducer';
import { selectActiveProductSlug } from 'store/productsReducer';
import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import Checkbox from 'components/Input/Checkbox';
import Radio from 'components/Input/Radio';
import { IOption } from 'components/Input/Radio/types';
import ModalBalanceRefill from 'components/Modals/ModalBalanceRefill';
import ModalConfirm from 'components/Modals/ModalConfirm';
import ModalInfo from 'components/Modals/ModalInfo';
import Tooltip from 'components/Tooltip';
import { ReactComponent as MenuIcon } from 'assets/imgs/menu-dots.svg';
import classes from './advertisingCampaignPayment.module.scss';

const CAMPAIGNS_TYPES_TEXT: { [key: string]: string } = {
	[CAMPAIGNS_TYPES.GEO]: 'Приоритетное размещение в Картах и Навигаторе',
	[CAMPAIGNS_TYPES.SUBSCRIPTION]: 'Рекламная подписка',
	[CAMPAIGNS_TYPES.WEB]: 'Реклама сайта или соцсети',
};

const BUDGET_INFO_TEXT = {
	[CAMPAIGNS_BUDGET.WEB_MINIMAL]:
		'Минимальный бюджет. Скорее всего эффект от кампании будет слабым.\nПотенциальный клиент - тот кто перешел на ваш сайт.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
	[CAMPAIGNS_BUDGET.WEB_OPTIMAL]:
		'Оптимальный бюджет. Хорошее соотношение затрат и эффективности. Такой бюджет выбирает большинство похожих организаций.\nПотенциальный клиент - тот кто перешел на ваш сайт.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
	[CAMPAIGNS_BUDGET.WEB_MAXIMAL]:
		'Максимальный бюджет. Рекламу увидит больше потенциальных клиентов. Ваш бюджет выше чем у значительной части конкурентов.\nПотенциальный клиент - тот кто перешел на ваш сайт.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
	[CAMPAIGNS_BUDGET.MINIMAL]:
		'Минимальный бюджет. Скорее всего эффект от кампании будет слабым.\nПотенциальный клиент — тот, кто заинтересовался и открыл карточку организации.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
	[CAMPAIGNS_BUDGET.OPTIMAL]:
		'Оптимальный бюджет. Хорошее соотношение затрат и эффективности. Такой бюджет выбирает большинство похожих организаций.\nПотенциальный клиент — тот, кто заинтересовался и открыл карточку организации.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
	[CAMPAIGNS_BUDGET.MAXIMAL]:
		'Максимальный бюджет. Рекламу увидит больше потенциальных клиентов. Ваш бюджет выше чем у значительной части конкурентов.\nПотенциальный клиент — тот, кто заинтересовался и открыл карточку организации.\nПрогноз основан на данных об аналогичных размещениях в прошлом',
};

const BUDGET_TYPES: { [key: string]: CampaignPriceTypes } = {
	[CAMPAIGNS_TYPES.GEO]: 'geoproduct',
	[CAMPAIGNS_TYPES.SUBSCRIPTION]: 'subscription',
	[CAMPAIGNS_TYPES.WEB]: 'websubscription',
};

const BUDGET_TYPES_TEXT: { [key: string]: string } = {
	[CAMPAIGNS_BUDGET.WEB_MINIMAL]: 'Минимальный',
	[CAMPAIGNS_BUDGET.WEB_OPTIMAL]: 'Оптимальный',
	[CAMPAIGNS_BUDGET.WEB_MAXIMAL]: 'Максимальный',
	[CAMPAIGNS_BUDGET.MINIMAL]: 'Минимальный',
	[CAMPAIGNS_BUDGET.OPTIMAL]: 'Оптимальный',
	[CAMPAIGNS_BUDGET.MAXIMAL]: 'Максимальный',
};

const AdvertisingCampaignPayment: React.FC = () => {
	const { campaignId = '' } = useParams();
	const navigate = useNavigate();
	const statusCheckTimer = useRef<any>(null);
	const balance = useSelector(selectBalance);
	const activeProductSlug = useSelector(selectActiveProductSlug);
	const companyPartner = useSelector(selectCompanyProduct);
	const companySubclientId = useSelector(selectCompanyProductSubclientId);
	const hasCompanyRights = useSelector(selectHasCompanyRights);
	const [advertisingCampaign, setAdvertisingCampaign] =
		useState<ICampaign | null>(null);
	const [pricesData, setPricesData] = useState<IOption[]>([]);
	const [selectedBudget, setSelectedBudget] = useState('');
	const [selectedPeriod, setSelectedPeriod] = useState('');
	const [promo, setPromo] = useState('');
	const [promoError, setPromoError] = useState('');
	const [isPromoActivated, setIsPromoActivated] = useState(false);
	const [isStartCampaign, setIsStartCampaign] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isLoadingPromocode, setIsLoadingPromocode] = useState(false);
	const [isLoadingPay, setIsLoadingPay] = useState(false);
	const [modalOpen, setModalOpen] = useState<string>('');
	const [balanceToRefill, setBalanceToRefill] = useState('');
	const [isOpenLinks, setIsOpenLinks] = useState(false);

	const isGeoType = advertisingCampaign?.meta?.type === CAMPAIGNS_TYPES.GEO;
	const activePriceData = pricesData.find(
		(priceData) => priceData.value === selectedBudget
	);
	const activePeriodData = activePriceData?.periodOptions.find(
		(periodData: IOption) => periodData.value === selectedPeriod
	);
	const isConfirmModal = modalOpen === 'confirm';
	const campaignStatus =
		advertisingCampaign?.campaign_partner_info?.status || '';
	const isNotStarted = campaignStatus === CAMPAIGNS_STATUSES.WAITING;
	const isArchive =
		campaignStatus === CAMPAIGNS_STATUSES.FINISHED ||
		campaignStatus === CAMPAIGNS_STATUSES.STOPPED;
	const productCampaignId = advertisingCampaign?.meta?.id || '';

	const getDiscountDescription = (priceData: IPrices) => {
		if (priceData.promoBonus) {
			return `+${priceData.promoBonus} бонусных рублей`;
		}
		if (priceData.promoDiscount) {
			return `Вместо ${priceData.amountWithoutPromoDiscount}₽`;
		}
		return '';
	};

	const getPeriodOptions = (
		budgetTypeData?: ICampaignPrice,
		isPromoActivated?: boolean
	): IOption[] => {
		const periodOptions: IOption[] = [];
		const durations = budgetTypeData?.durations;
		if (durations) {
			Object.keys(durations).forEach((periodTypeKey) => {
				const duration =
					durations[periodTypeKey as CampaignPeriodTypes];
				if (periodTypeKey !== 'monthPrediction') {
					periodOptions.push({
						value: periodTypeKey,
						label: `${periodTypeKey} дней - ${duration.amount}₽`,
						description: isPromoActivated
							? getDiscountDescription(duration)
							: '',
						...duration,
					});
				}
			});
		}
		return periodOptions;
	};

	const getBudgetOption = (
		budgetTypeKey: CampaignTypes,
		budgetTypeData?: ICampaignPrice,
		isPromoActivated?: boolean
	) => ({
		value: budgetTypeKey,
		label: BUDGET_TYPES_TEXT[budgetTypeKey],
		description: `Примерно ${budgetTypeData?.monthPrediction} потенциальных клиентов в месяц`,
		periodOptions: getPeriodOptions(budgetTypeData, isPromoActivated),
	});

	const formatePriceData = (
		newPriceData: ICampaignPrices,
		campaignType: string,
		isPromoActivated?: boolean
	) => {
		const advertisingCampaignType = campaignType || CAMPAIGNS_TYPES.GEO;
		const budgetType: CampaignPriceTypes =
			BUDGET_TYPES[advertisingCampaignType];
		const budgetTypeData: ICampaignPriceData =
			newPriceData?.data?.[budgetType] || {};
		const budgetOptions = Object.keys(budgetTypeData).map((budgetTypeKey) =>
			getBudgetOption(
				budgetTypeKey as CampaignTypes,
				budgetTypeData[budgetTypeKey as CampaignTypes],
				isPromoActivated
			)
		);
		return budgetOptions;
	};

	const getCampaignPrices = async (
		campaign: ICampaign | null,
		isPromoActivated?: boolean
	): Promise<IOption[]> => {
		if (!campaign) return [];

		const companyId = companyPartner?.id || '';
		const countryGeoId = companyPartner?.countryGeoId || '';
		let formattedPriceData: IOption[] = [];
		try {
			const { data: newPricesData } = await yandexAdvertisingPrice({
				id: campaign?.id,
				branding: campaign?.meta?.type === CAMPAIGNS_TYPES.GEO,
				campaignId: campaign?.meta?.id,
				chainId: 0,
				companyId,
				countryGeoId,
			});
			formattedPriceData = formatePriceData(
				newPricesData,
				campaign?.meta?.type,
				isPromoActivated
			);
			setPricesData(formattedPriceData);
		} catch (e) {
			console.log(e);
		}
		return formattedPriceData;
	};

	const getCampaignPricesData = async (
		campaign: ICampaign | null,
		isPromoActivated?: boolean
	) => {
		const newPricesData = await getCampaignPrices(
			campaign,
			isPromoActivated
		);
		const newActivePriceData =
			(!isGeoType && newPricesData[1]) || newPricesData[0];
		const newSelectedPeriod =
			newActivePriceData.periodOptions[1] ||
			newActivePriceData.periodOptions[0];
		setSelectedBudget(newActivePriceData.value);
		setSelectedPeriod(newSelectedPeriod.value);
	};

	const getCampaignPromo = async (campaign: ICampaign): Promise<boolean> => {
		const campaignPromo = campaign?.campaign_partner_info
			?.campaignPromo || {
			applicable: false,
			promocode: '',
		};
		let isPromoApplied = !!(
			campaignPromo?.applicable && campaignPromo?.promocode
		);
		let newPromo = campaignPromo?.promocode || '';
		const notAppliedPromo = campaign?.promocode?.value || '';
		if (notAppliedPromo && !isPromoApplied) {
			try {
				const { data: promocodeData } = await yandexActivatePromocode({
					promocode: notAppliedPromo,
					advert_campaign_id: Number(campaignId),
				});
				newPromo = promocodeData.applicable ? notAppliedPromo : '';
				isPromoApplied = promocodeData.applicable;
			} catch (e) {
				console.log(e);
			}
		}
		setPromo(newPromo);
		setIsPromoActivated(isPromoApplied);
		return isPromoApplied;
	};

	const getCampaignData = async (): Promise<void> => {
		setIsLoading(true);
		try {
			const { data } = await checkAdvertisingCampaignStatus(
				activeProductSlug,
				campaignId
			);
			if (data.state === 'complete') {
				const { data: newAdvertisingCampaign } =
					await getAdvertisingCampaignById(
						activeProductSlug,
						campaignId
					);
				setAdvertisingCampaign(newAdvertisingCampaign);
				const isPromoApplied = await getCampaignPromo(
					newAdvertisingCampaign
				);
				await getCampaignPricesData(
					newAdvertisingCampaign,
					isPromoApplied
				);
				setIsLoading(false);
			} else {
				statusCheckTimer.current = setTimeout(() => {
					getCampaignData();
				}, 2000);
			}
		} catch (err) {
			console.log(err);
		}
	};

	const handleToggleLinks = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setIsOpenLinks(!isOpenLinks);
	};

	const handleOutsideClick = () => {
		setIsOpenLinks(false);
	};

	useEffect(() => {
		if (companyPartner && activeProductSlug) {
			getCampaignData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyPartner, activeProductSlug]);

	useEffect(() => {
		setIsStartCampaign(hasCompanyRights);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasCompanyRights]);

	useEffect(() => {
		return () => {
			if (statusCheckTimer.current) {
				clearTimeout(statusCheckTimer.current);
			}
		};
	}, []);

	useEffect(() => {
		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleChange = (value: string): void => {
		setPromo(value);
		setPromoError('');
	};

	const handleApplyPromo = async (): Promise<void> => {
		if (promo === '' || promoError) return;

		setIsLoadingPromocode(true);
		try {
			const { data: promocodeData } = await yandexActivatePromocode({
				promocode: promo,
				advert_campaign_id: Number(campaignId),
			});
			if (!promocodeData?.applicable) {
				setIsLoadingPromocode(false);
				setPromoError('Неверный промокод');
				return;
			}
			setIsPromoActivated(true);
			getCampaignPrices(advertisingCampaign, true);
		} catch {
			setPromoError('Ошибка. Повторите позже');
		}
		setIsLoadingPromocode(false);
	};

	const handleCancelPromo = async (): Promise<void> => {
		if (promo === '' || promoError) return;

		setIsLoadingPromocode(true);
		try {
			await yandexDeactivatePromocode({
				promocode: promo,
				advert_campaign_id: Number(campaignId),
			});
			setIsPromoActivated(false);
			handleChange('');
			getCampaignPrices(advertisingCampaign, false);
		} catch {
			setPromoError('Не удалось отменить промокод');
		}
		setIsLoadingPromocode(false);
	};

	const handleSubmit = () => {
		const campaignPrice = Number(activePeriodData?.amount);
		const currentBalance = Number(balance);
		let modalType = 'confirm';
		if (currentBalance < campaignPrice) {
			modalType = 'payment';
		}
		setModalOpen(modalType);
	};

	const handleCloseModal = () => {
		setModalOpen('');
	};

	const handleCloseModalInfo = () => {
		handleCloseModal();
		navigate('../../');
	};

	const handleStart = async (): Promise<void> => {
		try {
			await startAdvertisingCampaign({
				slug: activeProductSlug,
				id: advertisingCampaign?.id || '',
				payload: {
					partner_type: 'yandex',
					budgetType: selectedBudget,
					duration: selectedPeriod,
				},
			});
			toast.success('Рекламная кампания успешно запущенна');
		} catch (e) {
			toast.error('Не удалось запустить рекламную кампанию');
		}
		setIsLoadingPay(false);
		handleCloseModal();
		setModalOpen('info');
	};

	const handlePay = async (): Promise<void> => {
		try {
			setIsLoadingPay(true);
			await payAdvertisingCampaign({
				slug: activeProductSlug,
				id: advertisingCampaign?.id || '',
				payload: {
					subclient_id: companySubclientId,
					campaign_type:
						BUDGET_TYPES[advertisingCampaign?.meta?.type || ''],
					campaign_subtype: isGeoType ? 'DEFAULT' : selectedBudget,
					duration: selectedPeriod,
					branding: isGeoType,
					geo_id: companyPartner?.countryGeoId || '',
				},
			});
			toast.success('Рекламная кампания оплачена');
			if (isStartCampaign) {
				return handleStart();
			}
			setIsLoadingPay(false);
			handleCloseModal();
			setModalOpen('info');
		} catch (e) {
			setIsLoadingPay(false);
			toast.error('Не удалось оплатить рекламную кампанию');
		}
	};

	const handleConfirm = () => {
		if (isConfirmModal) {
			return handlePay();
		}
		const campaignPrice = Number(activePeriodData?.amount);
		const currentBalance = Number(balance);
		setBalanceToRefill(`${campaignPrice - currentBalance}`);
		setModalOpen('balanceRefill');
	};

	const campaignInfoData: { [key: string]: string | number } = {
		'Название организации': companyPartner?.name || '',
		'Название рекламной кампании': advertisingCampaign?.name || '',
		'ID рекламной кампании': productCampaignId,
		'Тип рекламы':
			CAMPAIGNS_TYPES_TEXT[advertisingCampaign?.meta?.type || ''],
	};
	if (advertisingCampaign?.meta?.type === CAMPAIGNS_TYPES.WEB) {
		campaignInfoData['Адрес сайта'] = advertisingCampaign?.meta?.url || '';
	}

	const paymentInfoData: { [key: string]: string } =
		isPromoActivated && activePeriodData?.promoBonus
			? {
					'Полная стоимость': activePeriodData?.amount,
					'Дополнительный бюджет': activePeriodData?.promoBonus,
					'Остаток на счете': balance,
			  }
			: {
					'Полная стоимость': activePeriodData?.amount,
					'Скидка по промокоду': isPromoActivated
						? activePeriodData?.promoDiscount
						: '--',
					'Остаток на счете': balance,
			  };

	const header = (
		<div className={classes.header}>
			<div className={classes.title}>Оплата рекламной кампании</div>
			<div className={classes.menuIcon} onClick={handleToggleLinks}>
				<MenuIcon />
				{isOpenLinks && (
					<div className={classes.menu}>
						{!isArchive && (
							<a
								href={`https://yandex.ru/business/priority/campaign/${productCampaignId}/media`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Добавить рекламные материалы
							</a>
						)}
						{!isArchive && (
							<a
								href={`https://yandex.ru/business/subscription/campaign/${productCampaignId}/tuning/business-card`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Настроить параметры показа рекламы
							</a>
						)}
						{!isNotStarted && (
							<a
								href={`https://yandex.ru/business/priority/campaign/${productCampaignId}/statistic`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Статистика рекламной кампании
							</a>
						)}
						<a
							href={`https://yandex.ru/sprav/${
								companyPartner?.id || ''
							}`}
							target="_blank"
							rel="noreferrer"
							className={classes.menuItem}
						>
							Информация об организации
						</a>
					</div>
				)}
			</div>
		</div>
	);

	const campaignInfo = (
		<Card className={classes.card}>
			{Object.keys(campaignInfoData).map((info: string) => (
				<div className={classes.info} key={info}>
					<div className={classes.infoTitle}>{info}</div>
					<div className={classes.infoText}>
						{campaignInfoData[info]}
					</div>
				</div>
			))}
		</Card>
	);

	const getDiscountInfo = (option: IOption) => {
		if (option.promoBonus) {
			return `(Общий бюджет кампании ${
				Number(option.amountWithoutPromoDiscount) +
				Number(option.promoBonus)
			}₽)`;
		}
		if (option.promoDiscount) {
			return `(скидка ${Math.round(
				(option?.promoDiscount / option?.amountWithoutPromoDiscount) *
					100
			)}%)`;
		}
		return '';
	};

	const renderRadioLabel = (type: string) => (option: IOption) => {
		return (
			<div className={classes.option}>
				<div className={classes.optionLabel}>{option.label}</div>
				{option.description && (
					<div className={classes.optionDescription}>
						{option.description}
						{type === 'period' && isPromoActivated && (
							<div className={classes.discountPercent}>
								{getDiscountInfo(option)}
							</div>
						)}
					</div>
				)}
				{type === 'budget' && (
					<Tooltip
						className={classes.optionInfo}
						text={BUDGET_INFO_TEXT[option.value]}
					/>
				)}
			</div>
		);
	};

	const budgetForm = (
		<Card
			className={classes.card}
			title="Выберите бюджет"
			isLoading={isLoading}
		>
			<Radio
				className={classes.options}
				value={selectedBudget}
				options={pricesData}
				onChange={setSelectedBudget}
				renderLabel={renderRadioLabel('budget')}
			/>
		</Card>
	);

	const periodForm = (
		<Card
			className={classes.card}
			title="Период размещения"
			isLoading={isLoading}
		>
			<Radio
				className={classes.options}
				value={selectedPeriod}
				options={activePriceData?.periodOptions || []}
				onChange={setSelectedPeriod}
				renderLabel={renderRadioLabel('period')}
			/>
		</Card>
	);

	const promoForm = (
		<Card className={classes.card} title="Промокод" isLoading={isLoading}>
			<div className={classes.promo}>
				<Input
					className={classes.input}
					value={promo}
					onChange={handleChange}
					placeholder="Промокод"
					error={promoError}
					disabled={isPromoActivated || isLoadingPromocode}
					isLoading={isLoadingPromocode}
				/>
				<Button
					className={cn(classes.button, classes.button__promo)}
					text={isPromoActivated ? 'Отменить' : 'Применить'}
					onClick={
						isPromoActivated ? handleCancelPromo : handleApplyPromo
					}
					isLoading={isLoadingPromocode}
					styleType="secondary"
				/>
			</div>
		</Card>
	);

	const paymentInfo = (
		<Card
			className={classes.card}
			title={`Стоимость зa ${selectedPeriod} дней`}
			isLoading={isLoading}
		>
			{Object.keys(paymentInfoData).map((info: string) => (
				<div className={classes.info} key={info}>
					<div className={classes.infoTitle}>{info}</div>
					<div className={classes.infoText}>
						{paymentInfoData[info]}
					</div>
				</div>
			))}
			<div className={classes.start}>
				<Checkbox
					className={classes.checkbox}
					value={isStartCampaign}
					onChange={setIsStartCampaign}
					label="Запустить рекламу сейчас"
					disabled={!hasCompanyRights}
				/>
				<Tooltip
					className={classes.optionInfo}
					text="Реклама будет запущена сразу после подтверждения оплаты"
				/>
			</div>
		</Card>
	);

	return (
		<div className={classes.advertisingCampaignPayment}>
			{header}
			{campaignInfo}
			{!isGeoType && budgetForm}
			{periodForm}
			{promoForm}
			{!isLoading && (
				<>
					{paymentInfo}
					<Button
						className={classes.button}
						text="Оплатить"
						onClick={handleSubmit}
						disabled={isLoading}
					/>
				</>
			)}
			<ModalConfirm
				className={classes.modal}
				isOpen={modalOpen === 'confirm' || modalOpen === 'payment'}
				onClose={handleCloseModal}
				onConfirm={handleConfirm}
				title={isConfirmModal ? 'Вы уверены?' : 'Недостаточно средств'}
				text={
					isConfirmModal
						? 'Действительно ли вы хотите оплатить рекламную кампанию?'
						: 'На вашем балансе недостаточно средств. Для запуска кампании пополните баланс личного кабинета'
				}
				confirmButtonText={
					isConfirmModal ? 'Оплатить' : 'Пополнить баланс'
				}
				confirmButtonClassName={classes.confirmButton}
				cancelButtonClassName={classes.cancelButton}
				cancelButtonText="Отменить"
				isLoading={isLoadingPay}
			/>
			<ModalBalanceRefill
				className={classes.modal}
				isOpen={modalOpen === 'balanceRefill'}
				onClose={handleCloseModal}
				value={balanceToRefill}
			/>
			<ModalInfo
				className={classes.modal}
				isOpen={modalOpen === 'info'}
				onClose={handleCloseModalInfo}
				title="Рекламная кампания успешно создана"
				text={
					<div>
						Рекомендуем перейти в личный кабинет{' '}
						<a
							href="https://business.yandex.ru/"
							target="_blank"
							rel="noreferrer"
							className={classes.link}
						>
							Яндекс.Бизнес
						</a>
						, чтобы добавить необходимые материалы и завершить
						настройку рекламной кампании
					</div>
				}
				buttonText="Продолжить"
				buttonClassName={classes.infoButton}
			/>
		</div>
	);
};

export default AdvertisingCampaignPayment;
